import { Choice, Field } from '@micin-jp/chicken-schema'
import { memo, useCallback, useState } from 'react'

import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
  FieldLabel,
  FieldRoot,
  FieldSetContent,
  FieldSetLegend,
  FieldSetRoot,
  For,
  HStack,
  Radio,
  RadioGroup,
  Separator,
  Stack,
  Text,
  Textarea,
  toaster,
} from '@/components/ui'
import { generateGraphQLCacheKey, useGqlMutation } from '@/lib/api'
import { FragmentType, getFragmentData, graphql } from '@/lib/gql'
import { GetInquiryDocument } from '@/lib/gql/graphql'
import { parseChickenSchema } from '@/utils/parseChickenSchema'

const criteriaValueDialogTrialFragment = graphql(`
  fragment CriteriaValueDialogTrial on Trial {
    trialNumber
    latestCriteria {
      trialCriteriaUid
      criteriaSchema {
        schema
      }
    }
  }
`)

const criteriaValueDialogInquiryFragment = graphql(`
  fragment CriteriaValueDialogInquiry on Inquiry {
    inquiryUid
  }
`)

const editCriteriaValuesMutation = graphql(`
  mutation EditCriteriaValues($input: EditInquiryCriteriaValuesInput!) {
    editInquiryCriteriaValues(input: $input) {
      inquiryUid
    }
  }
`)

type Props = {
  trigger: React.ReactNode
  trial: FragmentType<typeof criteriaValueDialogTrialFragment>
  defaultCriteriaValues?: CriteriaValue[]
  inquiry?: FragmentType<typeof criteriaValueDialogInquiryFragment>
  editable?: boolean
  onSubmit: ((values: CriteriaValue[]) => void) | null
}

export type CriteriaValue = {
  fid: string
  value: string | string[]
}

const getMap = (values?: CriteriaValue[]) => {
  return new Map((values ?? []).map(({ fid, value }) => [fid, value]))
}

export const CriteriaValueInputDialog = ({
  trigger,
  trial,
  defaultCriteriaValues,
  inquiry,
  editable = true,
  onSubmit,
}: Props) => {
  const inquiryData = getFragmentData(
    criteriaValueDialogInquiryFragment,
    inquiry,
  )
  const trialData = getFragmentData(criteriaValueDialogTrialFragment, trial)

  const [valueMap, setValueMap] = useState<Map<string, string | string[]>>(
    getMap(defaultCriteriaValues),
  )

  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleChange = useCallback((fid: string, value: string | string[]) => {
    setValueMap(current => {
      const newMap = new Map(current)
      newMap.set(fid, value)
      return newMap
    })
  }, [])

  const { request: editCriteriaValues } = useGqlMutation(
    editCriteriaValuesMutation,
    {
      onSuccess: () => {
        toaster.create({
          type: 'success',
          title: '選択除外基準を保存しました',
        })
      },
      cacheKeysOnSuccess: [
        generateGraphQLCacheKey(GetInquiryDocument, {
          inquiryUid: inquiryData?.inquiryUid ?? '',
        }),
      ],
    },
  )

  const handleSubmit = async () => {
    setIsSubmitted(true)
    if (onSubmit !== null) {
      const values = Array.from(valueMap.entries()).map(([fid, value]) => ({
        fid,
        value,
      }))
      onSubmit(values)
      toaster.create({
        type: 'success',
        title: '選択除外基準を更新しました',
        description: 'メッセージを送信するまで保存されないのでご注意ください',
        duration: 10000,
      })
      return
    }

    if (!inquiryData) return
    await editCriteriaValues({
      input: {
        inquiryUid: inquiryData.inquiryUid,
        criteria: [
          {
            trialCriteriaUid: trialData.latestCriteria[0].trialCriteriaUid,
            values: Array.from(valueMap.entries()).map(([fid, value]) => ({
              fid,
              value,
              fieldIndex: 1,
            })),
          },
        ],
      },
    })
  }

  const handleReset = useCallback(() => {
    setValueMap(getMap(defaultCriteriaValues))
  }, [defaultCriteriaValues])

  const trialNumber = trialData.trialNumber

  const schema = parseChickenSchema(
    trialData.latestCriteria[0].criteriaSchema.schema,
  )

  const onlyLabelFields = schema.fields.every(
    field => field.typeDef.type === 'Label',
  )

  const fixedEditable = editable && !onlyLabelFields

  return (
    <DialogRoot
      size="lg"
      onOpenChange={e => {
        if (!e.open && !isSubmitted) {
          handleReset()
        }
        setIsSubmitted(false)
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader p="6" display="flex" flexDirection="column" gap="1">
          <DialogTitle>
            <HStack>
              {fixedEditable ? (
                <span>選択除外基準の入力</span>
              ) : (
                <span>選択除外基準</span>
              )}
              <span>試験ID {trialNumber}</span>
            </HStack>
          </DialogTitle>
          {fixedEditable && (
            <Text fontSize="sm">該当の項目を入力してください</Text>
          )}
        </DialogHeader>
        <Separator />
        <DialogBody>
          <Stack gap="2">
            {schema.fields.map(field => (
              <FieldContent
                key={field.fid}
                readonly={!fixedEditable}
                field={field}
                value={valueMap.get(field.fid) ?? ''}
                onChange={handleChange}
              />
            ))}
          </Stack>
        </DialogBody>
        <Separator />
        <DialogFooter p="6">
          <DialogTrigger asChild>
            {fixedEditable ? (
              <Button colorPalette="gray">キャンセル</Button>
            ) : (
              <Button variant="outline">閉じる</Button>
            )}
          </DialogTrigger>
          {fixedEditable && (
            <DialogTrigger asChild>
              <Button onClick={handleSubmit}>保存</Button>
            </DialogTrigger>
          )}
        </DialogFooter>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  )
}

type FieldContentProps = {
  readonly: boolean
  field: Field
  value: string | string[]
  onChange: (fid: string, value: string | string[]) => void
}
const strValue = (value: string | string[]) =>
  Array.isArray(value) ? value.join(', ') : value

const FieldContent = memo(function FieldContent({
  readonly,
  field,
  value,
  onChange,
}: FieldContentProps) {
  const typeDef = field.typeDef
  switch (typeDef.type) {
    case 'Checkbox':
      return (
        <CheckboxForm
          readonly={readonly}
          fieldName={field.name}
          choices={typeDef.choices}
          value={Array.isArray(value) ? value : []}
          onChange={value => {
            onChange(field.fid, value)
          }}
        />
      )
    case 'Radio':
      return (
        <RadioForm
          readonly={readonly}
          fieldName={field.name}
          choices={typeDef.choices}
          value={strValue(value)}
          onChange={value => {
            onChange(field.fid, value)
          }}
        />
      )
    case 'Textarea':
      return (
        <TextareaForm
          readonly={readonly}
          fieldName={field.name}
          value={strValue(value)}
          onChange={value => {
            onChange(field.fid, value)
          }}
        />
      )
    case 'Label':
      return (
        <LabelField fieldName={field.name} content={typeDef.content ?? ''} />
      )
    default:
      return null
  }
})

type CheckboxFormProps = {
  readonly: boolean
  fieldName: string
  choices: Choice[]
  value: string[]
  onChange: (value: string[]) => void
}
const CheckboxForm = ({
  readonly,
  fieldName,
  choices,
  value,
  onChange,
}: CheckboxFormProps) => {
  return (
    <FieldSetRoot>
      <CheckboxGroup
        value={value}
        onValueChange={onChange}
        gap="0"
        readOnly={readonly}
      >
        <FieldSetLegend
          px="M"
          py="SS"
          whiteSpace="pre-wrap"
          wordBreak="break-all"
        >
          {fieldName}
        </FieldSetLegend>
        <FieldSetContent p="M">
          <Stack gap="2">
            <For each={choices}>
              {choice => (
                <Checkbox
                  key={choice.cid}
                  value={choice.cid}
                  whiteSpace="pre-wrap"
                  wordBreak="break-all"
                >
                  {choice.name}
                </Checkbox>
              )}
            </For>
          </Stack>
        </FieldSetContent>
      </CheckboxGroup>
    </FieldSetRoot>
  )
}

type RadioFormProps = {
  readonly: boolean
  fieldName: string
  choices: Choice[]
  value: string
  onChange: (value: string) => void
}
const RadioForm = ({
  readonly,
  fieldName,
  choices,
  value,
  onChange,
}: RadioFormProps) => {
  return (
    <FieldSetRoot>
      <FieldSetLegend
        px="M"
        py="SS"
        whiteSpace="pre-wrap"
        wordBreak="break-all"
      >
        {fieldName}
      </FieldSetLegend>
      <RadioGroup
        readOnly={readonly}
        m="0"
        value={value}
        onValueChange={({ value }) => {
          if (!value) return
          onChange(value)
        }}
      >
        <FieldSetContent p="M">
          <Stack gap="2">
            <For each={choices}>
              {choice => (
                <Radio
                  key={choice.cid}
                  value={choice.cid}
                  whiteSpace="pre-wrap"
                  wordBreak="break-all"
                >
                  {choice.name}
                </Radio>
              )}
            </For>
          </Stack>
        </FieldSetContent>
      </RadioGroup>
    </FieldSetRoot>
  )
}

type TextareaFormProps = {
  readonly: boolean
  fieldName: string
  value: string
  onChange: (value: string) => void
}
const TextareaForm = ({
  readonly,
  fieldName,
  value,
  onChange,
}: TextareaFormProps) => {
  return (
    <FieldRoot gap="0">
      <FieldLabel px="M" py="SS" whiteSpace="pre-wrap" wordBreak="break-all">
        {fieldName}
      </FieldLabel>
      <Box p="M" w="full">
        <Textarea
          readOnly={readonly}
          value={value}
          onChange={e => onChange(e.target.value)}
        />
      </Box>
    </FieldRoot>
  )
}

type LabelFieldProps = {
  fieldName: string
  content: string
}
const LabelField = ({ fieldName, content }: LabelFieldProps) => {
  return (
    <Box>
      {/* labelと同じstyleをあてる */}
      <Text
        px="M"
        py="SS"
        as="span"
        fontSize="md"
        fontWeight="semibold"
        color="gray.600"
        whiteSpace="pre-wrap"
        wordBreak="break-all"
      >
        {fieldName}
      </Text>
      <Text p="M" whiteSpace="pre-wrap" wordBreak="break-all" fontSize="md">
        {content}
      </Text>
    </Box>
  )
}
